import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiPlus, FiGrid, FiCheckCircle, FiEye } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TableFilters, { SortableTh } from '../components/TableFilters';
import TarefaDetalhesModal from '../components/TarefaDetalhesModal';
import './Tarefas.css';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const Tarefas = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [tarefas, setTarefas] = useState([]);
  const [total, setTotal] = useState(0);
  const [condicionantes, setCondicionantes] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLicenca, setFilterLicenca] = useState('');
  const [filterEmpreendimento, setFilterEmpreendimento] = useState('');
  const [filterResponsavel, setFilterResponsavel] = useState('');
  const [filterCond, setFilterCond] = useState('');
  const [filterPrioridade, setFilterPrioridade] = useState('');
  const [filterVencimentoDe, setFilterVencimentoDe] = useState('');
  const [filterVencimentoAte, setFilterVencimentoAte] = useState('');
  const [sort, setSort] = useState('data_vencimento');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [selectedTarefa, setSelectedTarefa] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [comentarios, setComentarios] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [novoComentario, setNovoComentario] = useState('');
  const [showAddResponsavelForm, setShowAddResponsavelForm] = useState(false);
  const [addResponsavelId, setAddResponsavelId] = useState('');
  const [openBadgeDropdown, setOpenBadgeDropdown] = useState(null);
  const [formData, setFormData] = useState({
    condicionante_id: '',
    usuario_responsavel_id: '',
    titulo: '',
    descricao: '',
    data_vencimento: '',
    prioridade: 'media'
  });

  const carregarAuxiliares = useCallback(async () => {
    try {
      const [empRes, licencasRes] = await Promise.all([
        api.get('/api/empreendimentos'),
        api.get('/api/licencas').catch(() => ({ data: [] }))
      ]);
      const empList = Array.isArray(empRes.data) ? empRes.data : (empRes.data?.data || []);
      setEmpreendimentos(empList);
      setLicencas(licencasRes.data || []);
      const usuariosPromises = empList.map((emp) =>
        api.get(`/api/usuarios/empreendimento/${emp.id}`).catch(() => ({ data: [] }))
      );
      const usuariosRes = await Promise.all(usuariosPromises);
      setUsuarios(usuariosRes.flatMap((r) => r.data || []));
      try {
        const condRes = await api.get('/api/condicionantes');
        setCondicionantes(condRes.data || []);
      } catch {
        const licencas = await api.get('/api/licencas');
        const condPromises = licencas.map((lic) =>
          api.get(`/api/condicionantes?licenca_id=${lic.id}`).catch(() => ({ data: [] }))
        );
        const condRes = await Promise.all(condPromises);
        setCondicionantes(condRes.flatMap((r) => r.data || []));
      }
    } catch (err) {
      console.error('Erro ao carregar auxiliares:', err);
    }
  }, []);

  const carregarTarefas = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (filterStatus) params.set('status', filterStatus);
      if (filterLicenca) params.set('licenca_id', filterLicenca);
      if (filterEmpreendimento) params.set('empreendimento_id', filterEmpreendimento);
      if (filterResponsavel) params.set('responsavel_id', filterResponsavel);
      if (filterPrioridade) params.set('prioridade', filterPrioridade);
      if (filterCond) params.set('condicionante_id', filterCond);
      if (filterVencimentoDe) params.set('vencimento_de', filterVencimentoDe);
      if (filterVencimentoAte) params.set('vencimento_ate', filterVencimentoAte);
      params.set('sort', sort);
      params.set('order', order);
      params.set('page', page);
      params.set('limit', limit);
      const tarefasRes = await api.get(`/api/tarefas?${params}`);
      const data = tarefasRes.data;
      if (data && typeof data === 'object' && 'data' in data) {
        setTarefas(data.data || []);
        setTotal(data.total || 0);
      } else {
        setTarefas(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
      alert('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterLicenca, filterEmpreendimento, filterResponsavel, filterPrioridade, filterCond, filterVencimentoDe, filterVencimentoAte, sort, order, page, limit]);

  useEffect(() => { carregarAuxiliares(); }, [carregarAuxiliares]);
  useEffect(() => { carregarTarefas(); }, [carregarTarefas]);

  const carregarDados = () => carregarTarefas();

  const handleSort = (col) => {
    setSort(col);
    setOrder((o) => (sort === col && o === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/tarefas', formData);
      setShowModal(false);
      setFormData({ condicionante_id: '', usuario_responsavel_id: '', titulo: '', descricao: '', data_vencimento: '', prioridade: 'media' });
      carregarDados();
    } catch (error) {
      console.error('Erro ao criar tarefa:', error);
      alert(error.response?.data?.error || 'Erro ao criar tarefa');
    }
  };

  const handleAdicionarResponsavel = async (e) => {
    e.preventDefault();
    if (!selectedTarefa || !addResponsavelId) return;
    try {
      await api.post(`/api/tarefas/${selectedTarefa.id}/responsaveis`, { usuario_id: addResponsavelId });
      setResponsaveis((prev) => [...prev, { usuario_id: addResponsavelId, usuario_nome: usuarios.find((u) => u.id === addResponsavelId)?.nome }]);
      setShowAddResponsavelForm(false);
      setAddResponsavelId('');
      carregarDados();
      carregarDetalhesTarefa(selectedTarefa.id);
      alert('Responsável adicionado!');
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao adicionar responsável');
    }
  };

  const handleRemoverResponsavel = async (usuarioId) => {
    if (!selectedTarefa || !usuarioId) return;
    if (!window.confirm('Remover este responsável da tarefa?')) return;
    try {
      await api.delete(`/api/tarefas/${selectedTarefa.id}/responsaveis/${usuarioId}`);
      setResponsaveis((prev) => prev.filter((r) => r.usuario_id !== usuarioId));
      carregarDados();
      carregarDetalhesTarefa(selectedTarefa.id);
      alert('Responsável removido.');
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao remover responsável');
    }
  };

  const handleExcluirAndamento = async (comentarioId) => {
    if (!selectedTarefa || !window.confirm('Excluir este andamento?')) return;
    try {
      await api.delete(`/api/tarefas/${selectedTarefa.id}/comentarios/${comentarioId}`);
      setComentarios((prev) => prev.filter((c) => c.id !== comentarioId));
      carregarDetalhesTarefa(selectedTarefa.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir andamento');
    }
  };

  const handleAprovar = async (tarefa) => {
    try {
      await api.post(`/api/tarefas/${tarefa.id}/aprovar`);
      if (showDetalhesModal) fecharDetalhes();
      carregarDados();
      alert('Tarefa aprovada!');
    } catch (error) {
      console.error('Erro ao aprovar tarefa:', error);
      alert(error.response?.data?.error || 'Erro ao aprovar tarefa');
    }
  };

  const handleConcluir = async (tarefa) => {
    try {
      const novoStatus = tarefa.requires_approval ? 'done_pending_approval' : 'concluida';
      await api.put(`/api/tarefas/${tarefa.id}`, {
        ...tarefa,
        status: novoStatus
      });
      carregarDados();
      if (novoStatus === 'done_pending_approval') {
        alert('Tarefa concluída e aguardando aprovação!');
      } else {
        alert('Tarefa concluída!');
      }
    } catch (error) {
      console.error('Erro ao concluir tarefa:', error);
      alert(error.response?.data?.error || 'Erro ao concluir tarefa');
    }
  };

  const handleMudarStatus = async (tarefaId, novoStatus) => {
    try {
      await api.put(`/api/tarefas/${tarefaId}`, { status: novoStatus });
      setSelectedTarefa((prev) => (prev?.id === tarefaId ? { ...prev, status: novoStatus } : prev));
      setOpenBadgeDropdown(null);
      carregarDados();
      carregarDetalhesTarefa(tarefaId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const handleMudarPrioridade = async (tarefaId, novaPrioridade) => {
    try {
      await api.put(`/api/tarefas/${tarefaId}`, { prioridade: novaPrioridade });
      setSelectedTarefa((prev) => (prev?.id === tarefaId ? { ...prev, prioridade: novaPrioridade } : prev));
      setOpenBadgeDropdown(null);
      carregarDados();
      carregarDetalhesTarefa(tarefaId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar prioridade');
    }
  };

  const handleMudarVencimento = async (tarefaId, novaData) => {
    try {
      await api.put(`/api/tarefas/${tarefaId}`, { data_vencimento: novaData || null });
      setSelectedTarefa((prev) => (prev?.id === tarefaId ? { ...prev, data_vencimento: novaData } : prev));
      carregarDados();
      carregarDetalhesTarefa(tarefaId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar vencimento');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pendente: 'badge-secondary',
      em_andamento: 'badge-info',
      done_pending_approval: 'badge-warning',
      concluida: 'badge-success',
      atrasada: 'badge-danger',
      cancelada: 'badge-secondary'
    };
    return badges[status] || 'badge-secondary';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pendente: 'Pendente',
      em_andamento: 'Em Andamento',
      done_pending_approval: 'Aguardando Aprovação',
      concluida: 'Concluída',
      atrasada: 'Atrasada',
      cancelada: 'Cancelada'
    };
    return labels[status] || status;
  };

  const carregarDetalhesTarefa = useCallback(async (tarefaId) => {
    if (!tarefaId) return;
    try {
      const [docsRes, comRes, histRes] = await Promise.all([
        api.get(`/api/tarefas/${tarefaId}/documentos`),
        api.get(`/api/tarefas/${tarefaId}/comentarios`).catch(() => ({ data: [] })),
        api.get(`/api/tarefas/${tarefaId}/historico-completo`).catch(() => ({ data: [] }))
      ]);
      const docData = docsRes.data;
      setDocumentos(Array.isArray(docData) ? docData : (docData?.data ?? []));
      setComentarios(comRes.data || []);
      setHistorico(histRes.data || []);
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    }
  }, []);

  const abrirDetalhes = (tarefa) => {
    setSelectedTarefa(tarefa);
    setShowDetalhesModal(true);
    setShowAddResponsavelForm(false);
    setAddResponsavelId('');
    setNovoComentario('');
    carregarDetalhesTarefa(tarefa.id);
  };

  const fecharDetalhes = () => {
    setShowDetalhesModal(false);
    setSelectedTarefa(null);
    setDocumentos([]);
    setComentarios([]);
    setHistorico([]);
    setResponsaveis([]);
    setShowAddResponsavelForm(false);
    setOpenBadgeDropdown(null);
    carregarDados();
  };

  const handleAdicionarComentario = async (e) => {
    e.preventDefault();
    if (!novoComentario?.trim() || !selectedTarefa) return;
    try {
      await api.post(`/api/tarefas/${selectedTarefa.id}/comentarios`, { conteudo: novoComentario.trim() });
      setNovoComentario('');
      carregarDetalhesTarefa(selectedTarefa.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao adicionar comentário');
    }
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="tarefas-page">
      <div className="page-header">
        <h1 className="page-title">Tarefas</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button type="button" className="btn btn-outline" onClick={() => navigate('/tarefas/kanban')}>
            <FiGrid /> Ver em Kanban
          </button>
          {hasPermission('task', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Nova Tarefa
          </button>
          )}
        </div>
      </div>

      <TableFilters
        search={search}
        onSearchChange={setSearch}
        filterRows={[
          ['search', 'empreendimento', 'licenca', 'status', 'responsavel'],
          ['condicionante', 'prioridade', 'vencimento_de', 'vencimento_ate']
        ]}
        filters={[
          {
            key: 'empreendimento',
            label: 'Empreendimento',
            value: filterEmpreendimento,
            onChange: (v) => { setFilterEmpreendimento(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(empreendimentos.map((e) => ({ value: e.id, label: e.nome })))
            ]
          },
          {
            key: 'licenca',
            label: 'Licença',
            value: filterLicenca,
            onChange: (v) => { setFilterLicenca(v); setPage(1); },
            options: [
              { value: '', label: 'Todas' },
              ...(licencas.map((lic) => ({
                value: lic.id,
                label: `${lic.tipo || ''} ${lic.numero || ''}`.trim() || lic.empreendimento_nome || lic.id
              })))
            ]
          },
          {
            key: 'responsavel',
            label: 'Responsável',
            value: filterResponsavel,
            onChange: (v) => { setFilterResponsavel(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...([...new Map(usuarios.map((u) => [u.id, u])).values()].map((u) => ({ value: u.id, label: u.nome })))
            ]
          },
          {
            key: 'status',
            label: 'Status',
            value: filterStatus,
            onChange: (v) => { setFilterStatus(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              { value: 'pendente', label: 'Pendente' },
              { value: 'em_andamento', label: 'Em andamento' },
              { value: 'done_pending_approval', label: 'Aguardando aprovação' },
              { value: 'concluida', label: 'Concluída' },
              { value: 'atrasada', label: 'Atrasada' }
            ]
          },
          {
            key: 'condicionante',
            label: 'Condicionante',
            value: filterCond,
            onChange: (v) => { setFilterCond(v); setPage(1); },
            options: [
              { value: '', label: 'Todas' },
              ...(condicionantes.slice(0, 50).map((c) => ({ value: c.id, label: (c.descricao || '').substring(0, 40) + '...' })))
            ]
          },
          {
            key: 'prioridade',
            label: 'Prioridade',
            value: filterPrioridade,
            onChange: (v) => { setFilterPrioridade(v); setPage(1); },
            options: [
              { value: '', label: 'Todas' },
              { value: 'alta', label: 'Alta' },
              { value: 'media', label: 'Média' },
              { value: 'baixa', label: 'Baixa' }
            ]
          },
          {
            key: 'vencimento_de',
            label: 'Venc. de',
            type: 'date',
            value: filterVencimentoDe,
            onChange: (v) => { setFilterVencimentoDe(v); setPage(1); }
          },
          {
            key: 'vencimento_ate',
            label: 'Venc. até',
            type: 'date',
            value: filterVencimentoAte,
            onChange: (v) => { setFilterVencimentoAte(v); setPage(1); }
          }
        ]}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        searchPlaceholder="Buscar por título..."
      />

      {tarefas.length === 0 ? (
        <div className="empty-state">
          <p>Nenhuma tarefa encontrada.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh sortKey="titulo" currentSort={sort} currentOrder={order} onSort={handleSort}>Título</SortableTh>
                <th>Empreendimento</th>
                <th>Licença</th>
                <th>Condicionante</th>
                <th>Responsáveis</th>
                <SortableTh sortKey="data_vencimento" currentSort={sort} currentOrder={order} onSort={handleSort}>Vencimento</SortableTh>
                <SortableTh sortKey="prioridade" currentSort={sort} currentOrder={order} onSort={handleSort}>Prioridade</SortableTh>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {tarefas.map((tarefa) => (
                <tr key={tarefa.id}>
                  <td>
                    <button
                      type="button"
                      className="link-titulo-tarefa"
                      onClick={() => abrirDetalhes(tarefa)}
                    >
                      {tarefa.titulo}
                    </button>
                  </td>
                  <td>
                    {tarefa.empreendimento_id ? (
                      <Link to={`/empreendimentos/${tarefa.empreendimento_id}`} className="link-detalhes">{tarefa.empreendimento_nome || '-'}</Link>
                    ) : (tarefa.empreendimento_nome || '-')}
                  </td>
                  <td>
                    {tarefa.processo_licenciamento_id ? (
                      <Link to={`/processos-licenciamento/${tarefa.processo_licenciamento_id}`} className="link-detalhes">
                        Processo: {tarefa.processo_licenciamento_titulo}
                      </Link>
                    ) : tarefa.licenca_id ? (
                      <Link to={`/licencas/${tarefa.licenca_id}/validar`} className="link-detalhes">
                        {tarefa.licenca_numero ? `${tarefa.licenca_tipo || ''} ${tarefa.licenca_numero}`.trim() : tarefa.licenca_tipo || '-'}
                      </Link>
                    ) : (tarefa.licenca_numero ? `${tarefa.licenca_tipo || ''} ${tarefa.licenca_numero}`.trim() : tarefa.licenca_tipo || '-')}
                  </td>
                  <td>
                    {tarefa.processo_licenciamento_id ? (
                      'Prazo administrativo'
                    ) : tarefa.condicionante_id && tarefa.licenca_id ? (
                      <Link to={`/licencas/${tarefa.licenca_id}/validar`} className="link-detalhes">
                        {tarefa.condicionante_descricao ? `${tarefa.condicionante_descricao.substring(0, 50)}${tarefa.condicionante_descricao.length > 50 ? '...' : ''}` : '-'}
                      </Link>
                    ) : (
                      tarefa.condicionante_descricao ? `${tarefa.condicionante_descricao.substring(0, 50)}${tarefa.condicionante_descricao.length > 50 ? '...' : ''}` : '-'
                    )}
                  </td>
                  <td>
                    <div className="membros-trello membros-trello-inline">
                      {(() => {
                        const nomes = (tarefa.responsaveis_nomes || tarefa.responsavel_nome || '').split(',').map((n) => n.trim()).filter(Boolean);
                        return nomes.length > 0 ? (
                          nomes.map((nome, i) => (
                            <div key={`${nome}-${i}`} className="membro-avatar membro-avatar-sm" title={nome}>
                              {nome.charAt(0).toUpperCase()}
                            </div>
                          ))
                        ) : (
                          <span className="membro-empty">-</span>
                        );
                      })()}
                    </div>
                  </td>
                  <td>{formatDate(tarefa.data_vencimento)}</td>
                  <td>
                    <span className={`badge badge-${tarefa.prioridade === 'alta' ? 'danger' : tarefa.prioridade === 'media' ? 'warning' : 'secondary'}`}>
                      {tarefa.prioridade}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(tarefa.status_efetivo || tarefa.status)}`}>
                      {getStatusLabel(tarefa.status_efetivo || tarefa.status)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary btn-icon"
                        onClick={() => abrirDetalhes(tarefa)}
                        title="Visualizar detalhes"
                      >
                        <FiEye />
                      </button>
                      {((tarefa.status === 'pendente' || tarefa.status === 'em_andamento' || tarefa.status === 'atrasada') && hasPermission('task', 'complete')) ||
                       (tarefa.status === 'done_pending_approval' && hasPermission('task', 'approve')) ? (
                        <button
                          className="btn btn-sm btn-success btn-icon"
                          onClick={() => tarefa.status === 'done_pending_approval' ? handleAprovar(tarefa) : handleConcluir(tarefa)}
                          title={tarefa.status === 'done_pending_approval' ? 'Aprovar conclusão' : 'Concluir'}
                        >
                          <FiCheckCircle />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2><FiPlus /> Nova Tarefa</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Condicionante *</label>
                <select
                  required
                  value={formData.condicionante_id}
                  onChange={(e) => setFormData({ ...formData, condicionante_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {condicionantes.map((cond) => (
                    <option key={cond.id} value={cond.id}>
                      {cond.descricao.substring(0, 80)}...
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Responsável</label>
                <select
                  value={formData.usuario_responsavel_id}
                  onChange={(e) => setFormData({ ...formData, usuario_responsavel_id: e.target.value })}
                >
                  <option value="">Sem responsável</option>
                  {usuarios.map((usr) => (
                    <option key={usr.id} value={usr.id}>
                      {usr.nome} ({usr.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Título *</label>
                <input
                  type="text"
                  required
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Data de Vencimento</label>
                  <input
                    type="date"
                    value={formData.data_vencimento}
                    onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Prioridade</label>
                  <select
                    value={formData.prioridade}
                    onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })}
                  >
                    <option value="baixa">Baixa</option>
                    <option value="media">Média</option>
                    <option value="alta">Alta</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Criar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetalhesModal && selectedTarefa && (
        <TarefaDetalhesModal
          tarefa={selectedTarefa}
          onClose={fecharDetalhes}
          documentos={documentos}
          comentarios={comentarios}
          historico={historico}
          responsaveis={responsaveis}
          usuarios={usuarios}
          novoComentario={novoComentario}
          setNovoComentario={setNovoComentario}
          showAddResponsavelForm={showAddResponsavelForm}
          setShowAddResponsavelForm={setShowAddResponsavelForm}
          addResponsavelId={addResponsavelId}
          setAddResponsavelId={setAddResponsavelId}
          openBadgeDropdown={openBadgeDropdown}
          setOpenBadgeDropdown={setOpenBadgeDropdown}
          onAdicionarResponsavel={handleAdicionarResponsavel}
          onRemoverResponsavel={handleRemoverResponsavel}
          onExcluirAndamento={handleExcluirAndamento}
          onAprovar={handleAprovar}
          onMudarStatus={handleMudarStatus}
          onMudarPrioridade={handleMudarPrioridade}
          onMudarVencimento={handleMudarVencimento}
          onAdicionarComentario={handleAdicionarComentario}
          onRefreshDetalhes={carregarDetalhesTarefa}
          onDocumentAdded={(doc) => setDocumentos((prev) => [...(prev || []), doc])}
          hasPermission={hasPermission}
          user={user}
        />
      )}
    </div>
  );
};

export default Tarefas;
